import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { apiUrl } from '../utils/api';
import {
  DataGrid,
  GridRowModes,
  GridActionsCellItem,
  GridColDef
} from '@mui/x-data-grid';
import {
  Box,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Tooltip
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface AssignmentsProps {
  projectId: number | null;
  access?: string;
  deleteAllAccess?: string;
  userId: number;
}

const Assignments: React.FC<AssignmentsProps> = ({ projectId, access = 'none', deleteAllAccess = 'none', userId }) => {
  const readOnly = access !== 'write';
  const canDeleteAll = deleteAllAccess === 'write';
  const [assignments, setAssignments] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [newAssignment, setNewAssignment] = useState({
    change_number: '',
    document_number: '',
    responsible_user_id: '',
    assignment_affected: true,
    assignment_modelling_required: false,
    assignment_modelling_status: '',
    assignment_modelling_comment: '',
    assignment_document_status: ''
  });
  const [designOptions, setDesignOptions] = useState([]);
  const [documentOptions, setDocumentOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const fileRef = useRef(null);

  const [importSourceOpen, setImportSourceOpen] = useState(false);
  const [importSummaryOpen, setImportSummaryOpen] = useState(false);
  const [importSummaryData, setImportSummaryData] = useState<any>(null);
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState<any>(null);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mapFile, setMapFile] = useState(null);
  const [fileHeaders, setFileHeaders] = useState([]);
  const mappingFields = [
    'change_number',
    'document_number',
    'responsible_user',
    'assignment_affected',
    'assignment_modelling_required',
    'assignment_modelling_status',
    'assignment_modelling_comment',
    'assignment_document_status'
  ];
  const [mapping, setMapping] = useState<{ [key: string]: string }>(
    mappingFields.reduce((acc, f) => ({ ...acc, [f]: '' }), {})
  );

  useEffect(() => {
    if (!mapFile) { setFileHeaders([]); return; }
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const headers = [];
        const range = XLSX.utils.decode_range(ws['!ref']);
        const firstRow = range.s.r;
        for (let c = range.s.c; c <= range.e.c; c++) {
          const cell = ws[XLSX.utils.encode_cell({ r: firstRow, c })];
          headers.push(cell ? String(cell.v).trim() : `Column ${c + 1}`);
        }
        setFileHeaders(headers);
      } catch {
        setFileHeaders([]);
      }
    };
    reader.readAsArrayBuffer(mapFile);
  }, [mapFile]);

  const [addOpen, setAddOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const warningColorClass = 'warning-cell';

  const initialFilters = {
    assignment_id: [],
    change_number: [],
    document_number: [],
    responsible_user_id: [],
    assignment_affected: [],
    assignment_modelling_required: [],
    assignment_modelling_status: [],
    assignment_modelling_comment: [],
    assignment_document_status: []
  };

  const [filters, setFilters] = useState(initialFilters);


  const designFilter = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => `${option.change_number} ${option.change_name}`
  });
  const documentFilter = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => `${option.document_number} ${option.document_name}`
  });
  const userFilter = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => option.user_email
  });


  const fetchAssignments = useCallback(() => {
    fetch(apiUrl(`/api/assignments?project_id=${projectId}`))
      .then(res => res.json())
      .then(data => setAssignments(data))
      .catch(console.error);
  }, [projectId]);

  const fetchDesignChanges = useCallback(() => {
    fetch(apiUrl(`/api/design-changes?project_id=${projectId}`))
      .then(res => res.json())
      .then(data => {
        const filtered = data.filter(dc => dc.change_affecting_scope);
        setDesignOptions(filtered);
      })
      .catch(console.error);
  }, [projectId]);

  const fetchDocuments = useCallback(() => {
    fetch(apiUrl(`/api/documents?project_id=${projectId}`))
      .then(res => res.json())
      .then(data => setDocumentOptions(data))
      .catch(console.error);
  }, [projectId]);

  const fetchUsers = useCallback(() => {
    fetch(apiUrl('/api/users'))
      .then(res => res.json())
      .then(data => setUserOptions(data.users || []))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (access === 'none') return;
    if (!projectId) { setAssignments([]); setDesignOptions([]); setDocumentOptions([]); return; }
    fetchAssignments();
    fetchDesignChanges();
    fetchDocuments();
    fetchUsers();
  }, [access, projectId, fetchAssignments, fetchDesignChanges, fetchDocuments, fetchUsers]);

  const applyFilters = useCallback(
    (rows: any[], exclude?: string) =>
      rows.filter((r: any) => {
        for (const [field, selected] of Object.entries(filters)) {
          if (field === exclude || (selected as string[]).length === 0) continue;
          const value = r[field] == null ? '' : String(r[field]);
          if (!(selected as string[]).includes(value)) return false;
        }
        return true;
      }),
    [filters]
  );

  const filteredAssignments = useMemo(
    () => applyFilters(assignments, undefined),
    [assignments, applyFilters]
  );

  const getFilterOptions = field => {
    const opts = applyFilters(assignments, field).map(r =>
      r[field] == null ? '' : String(r[field])
    );
    return Array.from(new Set(opts));
  };

  const clearFilters = () => setFilters(initialFilters);

  const isDocumentStatusWarning = row => {
    if (row.assignment_affected && row.assignment_document_status === 'NA') return true;
    if (!row.assignment_affected && ['Pending', 'Submitted'].includes(row.assignment_document_status)) return true;
    return false;
  };

  const isModelStatusWarning = row => {
    if (row.assignment_modelling_required) {
      return !['Pending', 'Completed'].includes(row.assignment_modelling_status);
    }
    return row.assignment_modelling_status !== 'NA';
  };

  const handleDownloadTemplate = () => {
    fetch(apiUrl(`/api/assignments/template?project_id=${projectId}`))
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'assignments_template.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => alert('Failed to download template'));
  };
  const sendImport = (file, confirm, overwrite = false) => {
    const fd = new FormData();
    fd.append('file', file);
    return fetch(apiUrl(`/api/assignments/import?confirm=${confirm}&project_id=${projectId}&overwrite=${overwrite}`), {
      method: 'POST',
      headers: { 'X-User-ID': String(userId) },
      body: fd
    }).then(res => res.json());
  };

  const processImportFile = file => {
    sendImport(file, false)
      .then(summary => {
        if (summary.error) {
          setImportPreviewData({ error: summary.error, file, total: 0, importable: 0, duplicates: [], existing: [], invalid_documents: [] });
          setImportPreviewOpen(true);
          return;
        }
        setImportPreviewData({ ...summary, file });
        setImportPreviewOpen(true);
      })
      .catch(err => {
        setImportPreviewData({ error: err?.message || 'Import failed (server error)', file, total: 0, importable: 0, duplicates: [], existing: [], invalid_documents: [] });
        setImportPreviewOpen(true);
      });
  };

  const handlePreviewConfirm = (overwrite: boolean) => {
    const file = importPreviewData.file;
    const summary = importPreviewData;
    setImportPreviewOpen(false);
    sendImport(file, true, overwrite).then(result => {
      fetchAssignments();
      const overwrittenCount = result.updated ?? (overwrite ? summary.existing.length : 0);
      const skippedCount = result.skipped ?? (!overwrite ? summary.existing.length : 0);
      const failedCount = result.errors ?? ((summary.invalid_documents?.length || 0) + (summary.duplicates?.length || 0));
      const addedCount = result.created ?? ((result.imported || 0) - overwrittenCount);
      const rowStatuses = result.row_statuses ?? {};
      setImportSummaryData({
        total: result.total_processed ?? summary.total,
        added: addedCount,
        overwritten: overwrittenCount,
        skipped: skippedCount,
        failed: failedCount,
        rows: [
          ...(summary.existing || []).map(d => {
            const st = rowStatuses[d.row];
            return { label: `${d.change_number} / ${d.document_number}`, status: st === 'Overwritten' ? 'Overwritten' : 'Skipped (already exists)' };
          }),
          ...(summary.duplicates || []).map(d => ({ label: `${d.change_number} / ${d.document_number} (row ${d.row})`, status: 'Skipped (duplicate in file)' })),
          ...(summary.invalid_documents || []).map(r => ({ label: `Row ${r}`, status: 'Failed (invalid document)' })),
        ]
      });
      setImportSummaryOpen(true);
    }).catch(err => {
      setImportPreviewData({ error: err?.message || 'Import failed (server error)', file, total: 0, importable: 0, duplicates: [], existing: [], invalid_documents: [] });
      setImportPreviewOpen(true);
    });
  };

  const downloadPreviewLog = () => {
    if (!importPreviewData) return;
    const summary = importPreviewData;
    if (summary.error) {
      const file = summary.file;
      const reader = new FileReader();
      reader.onload = e => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const wb = XLSX.read(data, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][];
          const logRows = rows.slice(1).map((row, i) => ({
            label: `${row[0] != null ? String(row[0]).trim() : ''} / ${row[1] != null ? String(row[1]).trim() : ''} (row ${i + 2})`,
            status: `Failed: ${summary.error}`
          })).filter(r => r.label.trim() !== '/  ()');
          downloadImportLog({ rows: logRows });
        } catch {
          downloadImportLog({ rows: [{ label: 'Import error', status: `Failed: ${summary.error}` }] });
        }
      };
      reader.readAsArrayBuffer(file);
      return;
    }
    const previewRows = [
      ...(summary.existing || []).map(d => ({ label: `${d.change_number} / ${d.document_number}`, status: 'Skipped (already exists)' })),
      ...(summary.duplicates || []).map(d => ({ label: `${d.change_number} / ${d.document_number} (row ${d.row})`, status: 'Skipped (duplicate in file)' })),
      ...(summary.invalid_documents || []).map(r => ({ label: `Row ${r}`, status: 'Failed (invalid document)' })),
    ];
    downloadImportLog({ rows: previewRows });
  };

  const downloadImportLog = (data: any) => {
    const logRows = (data.rows || []).map((r: any, i: number) => {
      const s = r.status || ''
      let status: string, notes: string
      if (s === 'Overwritten') { status = 'Pass'; notes = 'Item overwritten' }
      else if (s.includes('Skipped') && !s.toLowerCase().includes('duplicate')) { status = 'Pass'; notes = 'Existing kept (skipped)' }
      else if (s.toLowerCase().includes('duplicate')) { status = 'Failed'; notes = 'Duplicate in file' }
      else { status = 'Failed'; notes = s.replace(/^Failed[:\s(]*/i, '').replace(/\)$/, '') }
      return { row_no: i + 2, item: r.label, status, notes }
    })
    fetch(apiUrl('/api/assignments/import-log'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows: logRows })
    })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = 'assignments_import_log.xlsx'; a.click()
        window.URL.revokeObjectURL(url)
      })
      .catch(() => alert('Failed to generate import log'))
  };

  const handleImportSource = isThird => {
    setImportSourceOpen(false);
    if (isThird) {
      setMappingOpen(true);
    } else {
      fileRef.current && fileRef.current.click();
    }
  };

  const handleMappingChange = field => (_, value) => {
    setMapping(prev => ({ ...prev, [field]: value || '' }));
  };

  const mapRowsToWorkbook = rows => {
    const data = [
      [
        'Change Number',
        'Document Number',
        'Responsible User',
        'Affected',
        'Model Req',
        'Model Status',
        'Model Comment',
        'Document Status'
      ],
      ...rows.map(r => [
        r.change_number || '',
        r.document_number || '',
        r.responsible_user || '',
        r.assignment_affected || '',
        r.assignment_modelling_required || '',
        r.assignment_modelling_status || '',
        r.assignment_modelling_comment || '',
        r.assignment_document_status || ''
      ])
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    return new File([wbout], 'mapped.xlsx', {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  };

  const handleMappingSubmit = () => {
    if (!mapFile) { alert('Select a file'); return; }
    const fd = new FormData();
    fd.append('file', mapFile);
    fd.append('mapping', JSON.stringify(mapping));
    fetch(apiUrl('/api/assignments/export-map'), { method: 'POST', body: fd })
      .then(res => res.json())
      .then(rows => {
        const file = mapRowsToWorkbook(rows);
        setMappingOpen(false);
        setMapFile(null);
        processImportFile(file);
      })
      .catch(() => alert('Failed to map file'));
  };

  const handleFileChange = e => {
    if (readOnly) return;
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    processImportFile(file);
  };

  const handleImportClick = () => {
    if (readOnly) return;
    setImportSourceOpen(true);
  };

  const rowsToWorkbook = rows => {
    const data = [
      [
        'Change Number',
        'Document Number',
        'Responsible User',
        'Affected',
        'Model Req',
        'Model Status',
        'Model Comment',
        'Document Status'
      ],
      ...rows.map(r => [
        r.change_number || '',
        r.document_number || '',
        userOptions.find(u => u.user_id === r.responsible_user_id)?.user_email || '',
        r.assignment_affected ? 'Yes' : 'No',
        r.assignment_modelling_required ? 'Yes' : 'No',
        r.assignment_modelling_status || '',
        r.assignment_modelling_comment || '',
        r.assignment_document_status || ''
      ])
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    return new File([wbout], 'assignments.xlsx', {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
  };

  const handleExport = () => {
    const rows = filteredAssignments;
    if (!window.confirm(`Export ${rows.length} rows?`)) return;
    const file = rowsToWorkbook(rows);
    const url = window.URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'assignments.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDeleteAll = () => {
    if (!canDeleteAll) return;
    if (!window.confirm('Delete ALL assignments?')) return;
    fetch(apiUrl(`/api/assignments/delete-all?project_id=${projectId}`), {
      method: 'DELETE',
      headers: { 'X-User-ID': String(userId) }
    })
      .then(res => {
        if (!res.ok) throw new Error('Delete all failed');
        fetchAssignments();
      })
      .catch(err => alert(err.message));
  };

  const handleAdd = () => {
    if (readOnly) return;
    const doc = documentOptions.find(d => d.document_number === newAssignment.document_number);
    const {
      change_number,
      responsible_user_id,
      assignment_affected,
      assignment_modelling_required,
      assignment_modelling_status,
      assignment_modelling_comment,
      assignment_document_status
    } = newAssignment;
    const payload = {
      change_number,
      document_id: doc ? doc.document_id : undefined,
      responsible_user_id: responsible_user_id || null,
      assignment_affected,
      assignment_modelling_required,
      assignment_modelling_status,
      assignment_modelling_comment,
      assignment_document_status
    };
    fetch(apiUrl('/api/assignments'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Add failed');
        return data;
      })
      .then(() => {
        setNewAssignment({
          change_number: '',
          document_number: '',
          responsible_user_id: '',
          assignment_affected: true,
          assignment_modelling_required: false,
          assignment_modelling_status: '',
          assignment_modelling_comment: '',
          assignment_document_status: ''
        });
        fetchAssignments();
      })
      .catch(err => alert(err.message));
  };

  const handleDelete = id => () => {
    if (readOnly) return;
    if (!window.confirm('Delete this assignment?')) return;
    fetch(apiUrl(`/api/assignments/${id}`), { method: 'DELETE' })
      .then(res => {
        if (!res.ok) throw new Error('Delete failed');
        fetchAssignments();
      })
      .catch(err => alert(err.message));
  };

  const handleEditClick = id => () => {
    if (readOnly) return;
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = id => () => {
    if (readOnly) return;
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = id => () => {
    if (readOnly) return;
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View, ignoreModifications: true } });
  };

  const processRowUpdate = updatedRow => {
    const {
      assignment_id,
      change_number,
      document_number,
      responsible_user_id,
      assignment_affected,
      assignment_modelling_required,
      assignment_modelling_status,
      assignment_modelling_comment,
      assignment_document_status
    } = updatedRow;
    const doc = documentOptions.find(d => d.document_number === document_number);
    const payload = {
      change_number,
      document_id: doc ? doc.document_id : undefined,
      responsible_user_id: responsible_user_id || null,
      assignment_affected,
      assignment_modelling_required,
      assignment_modelling_status,
      assignment_modelling_comment,
      assignment_document_status
    };
    return fetch(apiUrl(`/api/assignments/${assignment_id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Update failed');
        return data;
      })
      .then(() => { fetchAssignments(); return updatedRow; })
      .catch(err => { alert(err.message); throw err; });
  };

  const columns: GridColDef[] = [
    { field: 'assignment_id', headerName: 'ID', width: 80 },
    { field: 'change_number', headerName: 'Change #', flex: 1, editable: !readOnly },
    {
      field: 'document_number',
      headerName: 'Document #',
      flex: 1,
      editable: !readOnly,
      renderEditCell: params => (
        <Autocomplete
          options={documentOptions}
          getOptionLabel={o => o.document_number}
          filterOptions={documentFilter}
          value={documentOptions.find(o => o.document_number === params.value) || null}
          onChange={(_, val) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: val ? val.document_number : ''
            })
          }
          sx={{ width: '100%' }}
          renderInput={p => <TextField {...p} size="small" fullWidth />}
        />
      )
    },
    {
      field: 'responsible_user_id',
      headerName: 'User',
      flex: 1,
      editable: !readOnly,
      type: 'singleSelect',
      valueOptions: userOptions.map(u => ({ value: u.user_id, label: u.user_email }))
    },
    {
      field: 'assignment_affected',
      headerName: 'Affected',
      type: 'boolean',
      width: 100,
      editable: !readOnly,
      cellClassName: params =>
        isDocumentStatusWarning(params.row) ? warningColorClass : ''
    },
    {
      field: 'assignment_modelling_required',
      headerName: 'Model Req',
      type: 'boolean',
      width: 120,
      editable: !readOnly,
      cellClassName: params =>
        isModelStatusWarning(params.row) ? warningColorClass : ''
    },
    {
      field: 'assignment_modelling_status',
      headerName: 'Model Status',
      flex: 1,
      editable: !readOnly,
      type: 'singleSelect',
      valueOptions: ['NA', 'Pending', 'Completed'],
      cellClassName: params =>
        isModelStatusWarning(params.row) ? warningColorClass : ''
    },
    {
      field: 'assignment_modelling_comment',
      headerName: 'Comment',
      flex: 1.5,
      editable: !readOnly
    },
    {
      field: 'assignment_document_status',
      headerName: 'Document Status',
      flex: 1,
      editable: true,
      type: 'singleSelect',
      valueOptions: ['NA', 'Pending', 'Submitted'],
      cellClassName: params =>
        isDocumentStatusWarning(params.row) ? warningColorClass : ''
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: ({ id }) => {
        if (readOnly) return [];
        const isEditing = rowModesModel[id]?.mode === GridRowModes.Edit;
        if (isEditing) {
          return [
            <GridActionsCellItem icon={<Tooltip title="Save"><SaveIcon /></Tooltip>} label="Save" onClick={handleSaveClick(id)} showInMenu={false} key="save" />,
            <GridActionsCellItem icon={<Tooltip title="Cancel"><CancelIcon /></Tooltip>} label="Cancel" onClick={handleCancelClick(id)} showInMenu={false} key="cancel" />
          ];
        }
        return [
          <GridActionsCellItem icon={<Tooltip title="Edit"><EditIcon /></Tooltip>} label="Edit" onClick={handleEditClick(id)} showInMenu={false} key="edit" />,
          <GridActionsCellItem icon={<Tooltip title="Delete"><DeleteIcon /></Tooltip>} label="Delete" onClick={handleDelete(id)} showInMenu={false} key="delete" />
        ];
      }
    }
  ];

  if (access === 'none') {
    return <div>Access denied</div>;
  }

  if (!projectId) {
    return <div>Please select a project.</div>;
  }

  return (
    <div style={{ padding: 16 }}>

      {/* Responsive Add form with custom sizing */}
      {!readOnly && (
        <Accordion expanded={addOpen} onChange={() => setAddOpen(o => !o)} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Create Assignment</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ marginBottom: 20, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Box sx={{ flex: '2 1 200px' }}>
          <Autocomplete
            options={designOptions}
            getOptionLabel={o => o.change_number}
            filterOptions={designFilter}
            value={designOptions.find(o => o.change_number === newAssignment.change_number) || null}
            onChange={(_, val) => setNewAssignment({ ...newAssignment, change_number: val ? val.change_number : '' })}
            slotProps={{ popper: { sx: { '& .MuiPaper-root': { minWidth: 300 } } } }}
            renderInput={params => <TextField {...params} label="Change #" fullWidth />}
          />
              </Box>

              <Box sx={{ flex: '3 1 300px' }}>
          <Autocomplete
            options={documentOptions}
            getOptionLabel={o => o.document_number}
            filterOptions={documentFilter}
            value={documentOptions.find(o => o.document_number === newAssignment.document_number) || null}
            onChange={(_, val) => setNewAssignment({ ...newAssignment, document_number: val ? val.document_number : '' })}
            slotProps={{ popper: { sx: { '& .MuiPaper-root': { minWidth: 300 } } } }}
            renderInput={params => <TextField {...params} label="Document #" fullWidth />}
          />
              </Box>

              <Box sx={{ flex: '2 1 200px' }}>
          <Autocomplete
            options={userOptions}
            getOptionLabel={u => u.user_email}
            filterOptions={userFilter}
            value={userOptions.find(u => u.user_id === newAssignment.responsible_user_id) || null}
            onChange={(_, val) => setNewAssignment({ ...newAssignment, responsible_user_id: val ? val.user_id : '' })}
            slotProps={{ popper: { sx: { '& .MuiPaper-root': { minWidth: 300 } } } }}
            renderInput={params => <TextField {...params} label="User" fullWidth />}
          />
              </Box>

              <Box sx={{ minWidth: '80px' }}>
          <FormControlLabel
            control={<Checkbox checked={newAssignment.assignment_affected} onChange={e => setNewAssignment({ ...newAssignment, assignment_affected: e.target.checked })} />}
            label="Affected"
          />
              </Box>

              <Box sx={{ minWidth: '80px' }}>
          <FormControlLabel
            control={<Checkbox checked={newAssignment.assignment_modelling_required} onChange={e => setNewAssignment({ ...newAssignment, assignment_modelling_required: e.target.checked })} />}
            label="Model Req"
          />
              </Box>

              <Box sx={{ minWidth: '120px' }}>
          <TextField
            select
            label="Model Status"
            value={newAssignment.assignment_modelling_status}
            onChange={e => setNewAssignment({ ...newAssignment, assignment_modelling_status: e.target.value })}
            SelectProps={{ native: true }}
            fullWidth
          >
            {['NA', 'Pending', 'Completed'].map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </TextField>
              </Box>

              <Box sx={{ flex: '2 1 200px' }}>
          <TextField
            label="Comment"
            value={newAssignment.assignment_modelling_comment}
            onChange={e => setNewAssignment({ ...newAssignment, assignment_modelling_comment: e.target.value })}
            fullWidth
          />
              </Box>

              <Box sx={{ minWidth: '120px' }}>
          <TextField
            select
            label="Document Status"
            value={newAssignment.assignment_document_status}
            onChange={e => setNewAssignment({ ...newAssignment, assignment_document_status: e.target.value })}
            SelectProps={{ native: true }}
            fullWidth
          >
            {['NA', 'Pending', 'Submitted'].map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </TextField>
              </Box>

              <Button variant="contained" onClick={handleAdd} sx={{ height: '56px' }}>Add</Button>
            </div>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Filter row */}
      <Accordion expanded={filtersOpen} onChange={() => setFiltersOpen(o => !o)} sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Assignment Filters</Typography>
        </AccordionSummary>
        <AccordionDetails>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        {columns.filter(c => c.field !== 'actions').map(col => (
          <Box key={col.field} sx={{ flex: col.flex ? `${col.flex} 1 0` : undefined, width: col.width }}>
            <Autocomplete
              multiple
              size="small"
              options={getFilterOptions(col.field)}
              value={filters[col.field]}
              onChange={(_, val) => setFilters(f => ({ ...f, [col.field]: val }))}
              disableCloseOnSelect
              ListboxProps={{ sx: { fontSize: '0.8rem' } }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} sx={{ mr: 0.5 }} />
                  {option}
                </li>
              )}
              renderInput={params => (
                <TextField {...params} label={col.headerName} size="small" />
              )}
            />
          </Box>
        ))}
        <Box sx={{ width: columns.find(c => c.field === 'actions').width, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Button size="small" variant="outlined" onClick={clearFilters}>
            <FilterAltOffIcon />
          </Button>
        </Box>
      </div>
        </AccordionDetails>
      </Accordion>

      <div style={{ height: 550, width: '100%' }}>
        <DataGrid
            rows={filteredAssignments}
            columns={columns}
            getRowId={r => r.assignment_id}
            editMode={readOnly ? undefined : 'row'}
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            processRowUpdate={readOnly ? undefined : processRowUpdate}
        />
      </div>
      <div style={{ marginTop: 16 }}>
        <Button variant="outlined" onClick={handleDownloadTemplate} style={{ marginRight: 8 }}>
          Export Template
        </Button>
        <Button variant="contained" onClick={handleImportClick} disabled={readOnly} style={{ marginRight: 8 }}>
          Import From Excel
        </Button>
        <Button variant="contained" onClick={handleExport} style={{ marginRight: 8 }}>
          Export To Excel
        </Button>
        {canDeleteAll && (
          <Button variant="contained" color="error" onClick={handleDeleteAll} style={{ marginLeft: 8 }}>
            Delete All
          </Button>
        )}
        <input
          type="file"
          accept=".xlsx"
          ref={fileRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <Dialog open={importSourceOpen} onClose={() => setImportSourceOpen(false)}>
          <DialogTitle>Import Source</DialogTitle>
          <DialogContent>
            <Typography>Is this Excel file from a third-party source?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleImportSource(false)}>Template</Button>
            <Button variant="contained" onClick={() => handleImportSource(true)}>Third Party</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={mappingOpen} onClose={() => setMappingOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Column Mapping</DialogTitle>
          <DialogContent>
            <input type="file" accept=".xlsx" onChange={e => setMapFile(e.target.files[0])} style={{ marginBottom: 16 }} />
            {mappingFields.map(field => (
              <TextField
                key={field}
                select
                label={field.replace(/_/g, ' ')}
                value={mapping[field]}
                onChange={e => handleMappingChange(field)(null, e.target.value)}
                fullWidth
                margin="dense"
              >
                {fileHeaders.map(h => (
                  <MenuItem key={h} value={h}>
                    {h}
                  </MenuItem>
                ))}
              </TextField>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMappingOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleMappingSubmit}>Import</Button>
          </DialogActions>
        </Dialog>
        {/* Import Preview Dialog */}
        <Dialog open={importPreviewOpen} onClose={() => setImportPreviewOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{importPreviewData?.error ? 'Import Failed' : 'Import Preview'}</DialogTitle>
          <DialogContent>
            {importPreviewData && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                {importPreviewData.error && (
                  <Box sx={{ background: '#fff3f3', border: '1px solid #f44336', borderRadius: 1, p: '8px 12px' }}>
                    <Typography color="error.main" variant="body2">{importPreviewData.error}</Typography>
                  </Box>
                )}
                {!importPreviewData.error && (<>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Total rows in file:</Typography>
                  <Typography fontWeight={600}>{importPreviewData.total}</Typography>
                </Box>
                {importPreviewData.invalid_documents?.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="error.main">Invalid documents:</Typography>
                    <Typography fontWeight={600} color="error.main">{importPreviewData.invalid_documents.length}</Typography>
                  </Box>
                )}
                {importPreviewData.duplicates?.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="error.main">Duplicates in file:</Typography>
                    <Typography fontWeight={600} color="error.main">{importPreviewData.duplicates.length}</Typography>
                  </Box>
                )}
                {importPreviewData.existing?.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="warning.main">Already exist:</Typography>
                    <Typography fontWeight={600} color="warning.main">{importPreviewData.existing.length}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e0e0e0', pt: 1 }}>
                  <Typography fontWeight={600}>Rows to import:</Typography>
                  <Typography fontWeight={600}>{importPreviewData.importable}</Typography>
                </Box>
                </>)}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImportPreviewOpen(false)}>{importPreviewData?.error ? 'Close' : 'Cancel'}</Button>
            {!importPreviewData?.error && (importPreviewData?.existing?.length > 0 ? (
              <>
                <Button variant="outlined" onClick={() => handlePreviewConfirm(false)}>Import (Skip Existing)</Button>
                <Button variant="outlined" onClick={() => handlePreviewConfirm(true)}>Import (Overwrite)</Button>
              </>
            ) : (
              <Button variant="contained" onClick={() => handlePreviewConfirm(false)} disabled={!importPreviewData || importPreviewData.importable === 0}>
                Proceed with Import
              </Button>
            ))}
          </DialogActions>
        </Dialog>
        <Dialog open={importSummaryOpen} onClose={() => setImportSummaryOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Import Complete</DialogTitle>
          <DialogContent>
            {importSummaryData && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Total rows:</Typography>
                  <Typography fontWeight={600}>{importSummaryData.total}</Typography>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="success.main">New added:</Typography>
                  <Typography fontWeight={600} color="success.main">{importSummaryData.added}</Typography>
                </div>
                {importSummaryData.overwritten > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="warning.main">Overwritten:</Typography>
                    <Typography fontWeight={600} color="warning.main">{importSummaryData.overwritten}</Typography>
                  </div>
                )}
                {importSummaryData.skipped > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Skipped:</Typography>
                    <Typography fontWeight={600} color="text.secondary">{importSummaryData.skipped}</Typography>
                  </div>
                )}
                {importSummaryData.failed > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="error.main">Failed:</Typography>
                    <Typography fontWeight={600} color="error.main">{importSummaryData.failed}</Typography>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e0e0e0', paddingTop: 8 }}>
                  <Typography fontWeight={600}>Total changes:</Typography>
                  <Typography fontWeight={600}>{importSummaryData.added + importSummaryData.overwritten}</Typography>
                </div>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => downloadImportLog(importSummaryData)}>Download Import Log</Button>
            <Button variant="contained" onClick={() => setImportSummaryOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default Assignments;
